import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, BarChart3 } from "lucide-react";
import { Area, AreaChart, Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import ConsoleShell, { Breadcrumb } from "./ConsoleShell";
import { API, useAuth } from "./AuthContext";

const RANGES = [7, 30, 90];
const CONVERSION_LABELS = {
  briefing_request_submitted: "Briefing requests",
  deck_opened: "Deck opened",
  handbook_downloaded: "Handbook downloads",
};
const fmt = (n) => (n ?? 0).toLocaleString();
const shortDay = (d) => { const [, m, day] = d.split("-"); return `${Number(day)}/${Number(m)}`; };

function Kpi({ label, value, testid, accent }) {
  return (
    <div className="bg-white border border-[#E8E1D2] p-5" data-testid={testid}>
      <div className="font-mono text-[9.5px] uppercase tracking-[0.22em] text-[#A09680]">{label}</div>
      <div className={`font-serif text-4xl mt-2 leading-none ${accent ? "text-[#C84C21]" : "text-[#0F1825]"}`}>{fmt(value)}</div>
    </div>
  );
}

function BarList({ title, rows, keyName, testid, empty = "No data yet." }) {
  const max = Math.max(1, ...rows.map((r) => r.count));
  return (
    <section className="bg-white border border-[#E8E1D2] p-5" data-testid={testid}>
      <div className="font-mono text-[9.5px] uppercase tracking-[0.22em] text-[#A09680]">{title}</div>
      {rows.length === 0 ? <div className="mt-3 text-[13px] text-[#5C6470]">{empty}</div> : (
        <ul className="mt-3 space-y-2">
          {rows.map((r) => (
            <li key={r[keyName]} className="relative" data-testid={`${testid}-row`}>
              <div className="absolute inset-y-0 left-0 bg-[#FAF7F1]" style={{ width: `${(r.count / max) * 100}%` }} />
              <div className="relative flex items-baseline justify-between gap-3 px-2 py-1 text-[13px]">
                <span className="text-[#0F1825] truncate">{r[keyName]}</span>
                <span className="font-mono text-[11px] text-[#5C6470] shrink-0">{fmt(r.count)}</span>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

const tooltipStyle = { background: "#0F1825", border: "none", color: "#FAF7F1", fontSize: 12, fontFamily: "ui-monospace, monospace" };

export default function ConsoleAnalytics() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const isAdmin = (user?.role || "").toLowerCase() === "admin";

  useEffect(() => {
    if (!isAdmin) { toast.error("Analytics is admin-only.", { id: "analytics-admin-only" }); nav("/console/engagements", { replace: true }); }
  }, [isAdmin, nav]);

  useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;
    setLoading(true);
    axios.get(`${API}/admin/analytics`, { params: { days } })
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((e) => toast.error(e?.response?.data?.detail || "Could not load analytics."))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [days, isAdmin]);

  if (!isAdmin) return null;
  const conversions = data?.conversions || {};
  const downloads = Object.entries(data?.downloads || {}).filter(([, rows]) => rows.length > 0);

  return (
    <ConsoleShell hideIllustrativeRibbon breadcrumb={<Breadcrumb items={[{ label: "Engagements", to: "/console/engagements" }, { label: "Analytics" }]} />}>
      <div className="max-w-6xl mx-auto px-6 lg:px-10 py-12 space-y-6" data-testid="page-console-analytics">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#C84C21]">Admin · website</div>
            <h1 className="font-serif text-4xl md:text-5xl text-[#0F1825] mt-3 leading-[1.1] tracking-tight">Site analytics.</h1>
            <p className="mt-3 text-[#5C6470] text-[13px]" data-testid="analytics-privacy-note">
              Privacy-friendly first-party tracking — no cookies, no third parties. Admin pages excluded.
            </p>
          </div>
          <div className="inline-flex border border-[#E8E1D2] bg-white" role="group" data-testid="range-selector">
            {RANGES.map((r) => (
              <button
                key={r}
                type="button"
                onClick={() => setDays(r)}
                aria-pressed={days === r}
                className={`px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors ${days === r ? "bg-[#0F1825] text-white" : "text-[#0F1825] hover:text-[#C84C21]"}`}
                data-testid={`range-${r}`}
              >
                {r} days
              </button>
            ))}
          </div>
        </div>

        {loading && !data ? (
          <div className="flex items-center gap-2 text-[#5C6470] text-[13px]"><Loader2 size={14} className="animate-spin" /> Loading…</div>
        ) : data && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <Kpi label="Page views" value={data.pageviews} testid="kpi-pageviews" />
              <Kpi label="Unique visitors" value={data.visitors} testid="kpi-visitors" />
              {Object.keys(CONVERSION_LABELS).map((k) => (
                <Kpi key={k} label={CONVERSION_LABELS[k]} value={conversions[k]} testid={`kpi-${k}`} accent />
              ))}
            </div>

            <section className="bg-white border border-[#E8E1D2] p-5" data-testid="chart-daily">
              <div className="flex items-center justify-between">
                <div className="font-mono text-[9.5px] uppercase tracking-[0.22em] text-[#A09680]">Daily views vs visitors · {data.from} → {data.to}</div>
                <BarChart3 size={14} className="text-[#A09680]" />
              </div>
              <div className="h-64 mt-4">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={data.series} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
                    <defs>
                      <linearGradient id="gViews" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#0F1825" stopOpacity={0.35} /><stop offset="100%" stopColor="#0F1825" stopOpacity={0.02} /></linearGradient>
                      <linearGradient id="gVisitors" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#C84C21" stopOpacity={0.45} /><stop offset="100%" stopColor="#C84C21" stopOpacity={0.03} /></linearGradient>
                    </defs>
                    <CartesianGrid stroke="#E8E1D2" vertical={false} />
                    <XAxis dataKey="day" tickFormatter={shortDay} tick={{ fontSize: 10, fill: "#A09680" }} axisLine={false} tickLine={false} minTickGap={24} />
                    <YAxis tick={{ fontSize: 10, fill: "#A09680" }} axisLine={false} tickLine={false} allowDecimals={false} />
                    <Tooltip contentStyle={tooltipStyle} labelStyle={{ color: "#FAF7F1" }} />
                    <Area type="monotone" dataKey="views" name="Views" stroke="#0F1825" strokeWidth={1.5} fill="url(#gViews)" />
                    <Area type="monotone" dataKey="visitors" name="Visitors" stroke="#C84C21" strokeWidth={1.5} fill="url(#gVisitors)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </section>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <BarList title="Top pages" rows={data.pages} keyName="path" testid="list-pages" />
              <BarList title="Sources" rows={data.sources} keyName="source" testid="list-sources" />
              <BarList title="Countries" rows={data.countries} keyName="country" testid="list-countries" empty="No country headers seen yet." />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <section className="bg-white border border-[#E8E1D2] p-5" data-testid="chart-devices">
                <div className="font-mono text-[9.5px] uppercase tracking-[0.22em] text-[#A09680]">Devices</div>
                <div className="h-40 mt-3">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data.devices} margin={{ top: 4, right: 4, left: -24, bottom: 0 }}>
                      <CartesianGrid stroke="#E8E1D2" vertical={false} />
                      <XAxis dataKey="device" tick={{ fontSize: 10, fill: "#A09680" }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fontSize: 10, fill: "#A09680" }} axisLine={false} tickLine={false} allowDecimals={false} />
                      <Tooltip contentStyle={tooltipStyle} cursor={{ fill: "#FAF7F1" }} />
                      <Bar dataKey="count" name="Views" fill="#C84C21" radius={[2, 2, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </section>
              {downloads.map(([ev, rows]) => (
                <BarList key={ev} title={`${CONVERSION_LABELS[ev] || ev} · by path`} rows={rows} keyName="path" testid={`list-${ev}`} />
              ))}
            </div>
          </>
        )}
      </div>
    </ConsoleShell>
  );
}
